import { Alert, Badge, Button, cardClass, fieldClass, selectClass } from '@ledgerloom/ui';
import { Fragment, useState, type FormEvent, type ReactNode } from 'react';
import { ConfirmDialog } from './ConfirmDialog';
import { Field } from './Field';

// Model connections (docs/design/model-connections.md), shared by the operator
// console (the platform's) and Settings (a workspace's own). One card per
// provider; the form is inline in the card. Patterns: forms/password for the
// key, forms/checkbox for models and for each limit's "Unlimited", forms/form-
// validation, content-management/modal to confirm a disable.

export interface CatalogProvider { id: string; title: string; default_model: string; models: string[] }
export interface ModelConnection {
  id: string; provider: string; status: 'active' | 'disabled'; key_hint: string; base_url: string | null;
  models: string[]; default_model: string; prices: Record<string, [number, number]>;
  monthly_budget_micros: number | null; requests_per_minute: number | null; max_output_tokens: number | null; max_concurrent_runs: number | null;
  updated_at: string; key_rotated_at: string;
  usage: { calls_last_minute: number; runs_running: number; spent_month_micros: number };
}
export interface CheckResult { ok: boolean; models: string[]; error: string | null }
type Request = <T>(path: string, init?: { method?: string; body?: unknown }) => Promise<T>;

export const dollars = (micros: number) => `$${(micros / 1_000_000).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const LIMITS = [
  { key: 'monthly_budget_micros', label: 'Monthly spend', unit: 'dollars', hint: 'Runs wait in the queue once this month\'s spend reaches it.' },
  { key: 'requests_per_minute', label: 'Requests per minute', unit: 'requests', hint: 'A run waits for a free slot before each model call.' },
  { key: 'max_output_tokens', label: 'Output tokens per call', unit: 'tokens', hint: 'The most a model may write in one reply.' },
  { key: 'max_concurrent_runs', label: 'Runs at once', unit: 'runs', hint: 'More runs wait until one finishes.' },
] as const;
type LimitKey = (typeof LIMITS)[number]['key'];

function limitText(c: ModelConnection, key: LimitKey): string {
  const v = c[key];
  if (v === null) return 'Unlimited';
  return key === 'monthly_budget_micros' ? `${dollars(v)} a month` : key === 'requests_per_minute' ? `${v.toLocaleString()} a minute` : key === 'max_output_tokens' ? `${v.toLocaleString()} tokens` : `${v} at once`;
}

export function ConnectionCards({ catalog, connections, request, onChanged, canEdit, missing, extra }: {
  catalog: CatalogProvider[]; connections: ModelConnection[]; request: Request; onChanged: () => void; canEdit: boolean;
  /** What a provider with no connection here means for this owner. */
  missing: (p: CatalogProvider) => ReactNode;
  /** More about a connection, below its facts (the console: the credit left). */
  extra?: (p: CatalogProvider) => ReactNode;
}) {
  return (
    <div className="stack">
      {catalog.map((p) => <ConnectionCard key={p.id} provider={p} connection={connections.find((c) => c.provider === p.id) ?? null}
        request={request} onChanged={onChanged} canEdit={canEdit} missing={missing(p)} extra={extra?.(p)} />)}
    </div>
  );
}

function ConnectionCard({ provider, connection: c, request, onChanged, canEdit, missing, extra }: {
  provider: CatalogProvider; connection: ModelConnection | null; request: Request; onChanged: () => void; canEdit: boolean; missing: ReactNode; extra?: ReactNode;
}) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [check, setCheck] = useState<CheckResult | null>(null);
  const headId = `conn-${provider.id}-h`;

  const setStatus = async (status: 'active' | 'disabled') => {
    setBusy(true); setError(null);
    try { await request(`/${provider.id}/status`, { body: { status } }); onChanged(); } catch (e) { setError((e as Error).message); } finally { setBusy(false); setConfirming(false); }
  };
  const runCheck = async () => {
    setBusy(true); setError(null); setCheck(null);
    try { setCheck(await request<CheckResult>(`/${provider.id}/check`, { body: {} })); } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <section className={`${cardClass} card-pad stack`} aria-labelledby={headId} data-testid={`connection-${provider.id}`}>
      <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
        <h3 id={headId} className="grow" style={{ margin: 0 }}>{provider.title}</h3>
        {c ? <Badge tone={c.status === 'active' ? 'success' : 'warning'}>{c.status === 'active' ? 'Active' : 'Disabled'}</Badge> : <Badge>No connection</Badge>}
      </div>
      {error && <Alert tone="danger" role="alert">{error}</Alert>}
      {!c && !editing && <p className="faint" style={{ margin: 0 }}>{missing}</p>}
      {c && !editing && (
        <dl className="facts">
          <dt>Key</dt><dd className="mono">{c.key_hint}</dd>
          <dt>Models</dt><dd>{c.models.map((m) => <span key={m} className="mono">{m}{m === c.default_model && <Badge style={{ marginLeft: 4 }}>default</Badge>} </span>)}</dd>
          {c.base_url && <><dt>Endpoint</dt><dd className="mono">{c.base_url}</dd></>}
          {LIMITS.map((l) => <Fragment key={l.key}><dt>{l.label}</dt><dd>{limitText(c, l.key)}</dd></Fragment>)}
          <dt>This month</dt><dd>{dollars(c.usage.spent_month_micros)}{c.monthly_budget_micros !== null && ` of ${dollars(c.monthly_budget_micros)}`} · {c.usage.calls_last_minute} calls in the last minute · {c.usage.runs_running} running</dd>
        </dl>
      )}
      {c && !editing && extra}
      {check && (
        <Alert tone={check.ok ? 'success' : 'danger'} role="status">
          {check.ok ? `The key works. It reaches ${check.models.length} models.` : `The key did not work: ${check.error}`}
        </Alert>
      )}
      {editing
        ? <ConnectionForm provider={provider} existing={c} request={request} onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); setCheck(null); onChanged(); }} />
        : canEdit && (
          <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
            <Button type="submit" variant="secondary" onClick={() => setEditing(true)}>{c ? 'Change' : 'Add connection'}<span className="sr-only"> for {provider.title}</span></Button>
            {c && <Button type="submit" variant="secondary" disabled={busy} onClick={runCheck}>Check key<span className="sr-only"> for {provider.title}</span></Button>}
            {c && (c.status === 'active'
              ? <Button type="submit" variant="danger" disabled={busy} onClick={() => setConfirming(true)}>Disable<span className="sr-only"> {provider.title}</span></Button>
              : <Button type="submit" variant="secondary" disabled={busy} onClick={() => setStatus('active')}>Re-enable<span className="sr-only"> {provider.title}</span></Button>)}
          </div>
        )}
      <ConfirmDialog open={confirming} title={`Disable the ${provider.title} connection?`} confirmLabel="Disable connection" tone="danger" busy={busy}
        onConfirm={() => setStatus('disabled')} onCancel={() => setConfirming(false)}>
        <p style={{ margin: 0 }}>Runs stop using it at once. It is kept, with its key, until you re-enable it.</p>
      </ConfirmDialog>
    </section>
  );
}

interface FormState {
  apiKey: string; baseUrl: string; models: string[]; defaultModel: string;
  limits: Record<LimitKey, { unlimited: boolean; value: string }>;
  prices: Record<string, { input: string; output: string }>;
}

function initial(provider: CatalogProvider, c: ModelConnection | null): FormState {
  const limits = Object.fromEntries(LIMITS.map((l) => {
    const v = c?.[l.key] ?? null;
    return [l.key, { unlimited: v === null, value: v === null ? '' : String(l.key === 'monthly_budget_micros' ? v / 1_000_000 : v) }];
  })) as FormState['limits'];
  const prices = Object.fromEntries(Object.entries(c?.prices ?? {}).map(([m, [i, o]]) => [m, { input: String(i), output: String(o) }]));
  return { apiKey: '', baseUrl: c?.base_url ?? '', models: c?.models ?? [provider.default_model], defaultModel: c?.default_model ?? provider.default_model, limits, prices };
}

function ConnectionForm({ provider, existing, request, onCancel, onSaved }: {
  provider: CatalogProvider; existing: ModelConnection | null; request: Request; onCancel: () => void; onSaved: () => void;
}) {
  const [f, setF] = useState<FormState>(() => initial(provider, existing));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const validate = (): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!existing && f.apiKey.trim().length < 8) e.apiKey = 'Paste the API key for this account.';
    if (f.baseUrl && !/^https?:\/\//.test(f.baseUrl)) e.baseUrl = 'Start the address with https://';
    if (!f.models.length) e.models = 'Choose at least one model.';
    for (const l of LIMITS) {
      const s = f.limits[l.key];
      if (!s.unlimited && !(Number(s.value) > 0)) e[l.key] = `Enter a number above zero, or choose Unlimited.`;
      if (!s.unlimited && l.key !== 'monthly_budget_micros' && !Number.isInteger(Number(s.value))) e[l.key] = 'Enter a whole number.';
    }
    for (const [m, p] of Object.entries(f.prices)) {
      if (!f.models.includes(m) || (!p.input && !p.output)) continue;
      if (!(Number(p.input) >= 0) || !(Number(p.output) >= 0) || p.input === '' || p.output === '') e[`price-${m}`] = 'Enter both prices, or leave both empty for list prices.';
    }
    return e;
  };

  async function save(ev: FormEvent) {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    const limit = (k: LimitKey) => (f.limits[k].unlimited ? null : k === 'monthly_budget_micros' ? Math.round(Number(f.limits[k].value) * 1_000_000) : Number(f.limits[k].value));
    const prices = Object.fromEntries(Object.entries(f.prices).filter(([m, p]) => f.models.includes(m) && p.input !== '' && p.output !== '').map(([m, p]) => [m, [Number(p.input), Number(p.output)]]));
    setBusy(true); setFailed(null);
    try {
      await request(`/${provider.id}`, { method: 'PUT', body: {
        ...(f.apiKey.trim() ? { api_key: f.apiKey.trim() } : {}), base_url: f.baseUrl.trim() || null,
        models: f.models, default_model: f.models.includes(f.defaultModel) ? f.defaultModel : f.models[0],
        prices, ...Object.fromEntries(LIMITS.map((l) => [l.key, limit(l.key)])),
      } });
      onSaved();
    } catch (err) { setFailed((err as Error).message); } finally { setBusy(false); }
  }

  const toggleModel = (m: string, on: boolean) => {
    const models = on ? provider.models.filter((x) => x === m || f.models.includes(x)) : f.models.filter((x) => x !== m);
    setF({ ...f, models, defaultModel: models.includes(f.defaultModel) ? f.defaultModel : models[0] ?? '' });
  };
  const setLimit = (k: LimitKey, patch: Partial<{ unlimited: boolean; value: string }>) => setF({ ...f, limits: { ...f.limits, [k]: { ...f.limits[k], ...patch } } });

  return (
    <form className="stack" onSubmit={save} noValidate aria-label={`${provider.title} connection`}>
      {failed && <Alert tone="danger" role="alert">{failed}</Alert>}
      <Field label="API key" required={!existing} error={errors.apiKey}
        hint={existing ? `Leave empty to keep the key ending ${existing.key_hint.replace('…', '')}. A new key replaces it.` : 'Stored sealed. It is never shown again, only its last four characters.'}>
        <input className={fieldClass} type="password" autoComplete="off" spellCheck={false} value={f.apiKey} onChange={(e) => setF({ ...f, apiKey: e.target.value })} />
      </Field>
      <fieldset className="field" aria-describedby={errors.models ? `${provider.id}-models-err` : undefined}>
        <legend>Allowed models</legend>
        <div className="checks">
          {provider.models.map((m) => (
            <label key={m} className="check">
              <input type="checkbox" checked={f.models.includes(m)} onChange={(e) => toggleModel(m, e.target.checked)} />
              <span className="mono">{m}</span>
            </label>
          ))}
        </div>
        {errors.models && <div id={`${provider.id}-models-err`} className="field-error" role="alert">{errors.models}</div>}
      </fieldset>
      <Field label="Default model" hint="Used by an agent that names no model.">
        <select className={selectClass} value={f.defaultModel} disabled={!f.models.length} onChange={(e) => setF({ ...f, defaultModel: e.target.value })}>
          {f.models.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </Field>
      <fieldset className="field">
        <legend>Limits</legend>
        <div className="limit-grid">
          {LIMITS.map((l) => (
            <div key={l.key} className="limit-row">
              <Field label={`${l.label} (${l.unit})`} hint={l.hint} error={errors[l.key]}>
                <input className={fieldClass} type="number" inputMode="decimal" min={0} step={l.key === 'monthly_budget_micros' ? '0.01' : '1'}
                  disabled={f.limits[l.key].unlimited} value={f.limits[l.key].value} onChange={(e) => setLimit(l.key, { value: e.target.value })} />
              </Field>
              <label className="row small" style={{ gap: 8 }}>
                <input type="checkbox" checked={f.limits[l.key].unlimited} onChange={(e) => setLimit(l.key, { unlimited: e.target.checked })} />
                Unlimited<span className="sr-only"> {l.label.toLowerCase()}</span>
              </label>
            </div>
          ))}
        </div>
      </fieldset>
      <details>
        <summary>Endpoint and prices</summary>
        <div className="stack" style={{ marginTop: 12 }}>
          <Field label="Base URL" hint="Only for a gateway or proxy in front of the provider. Leave empty for the provider's own." error={errors.baseUrl}>
            <input className={fieldClass} type="url" value={f.baseUrl} placeholder="https://" onChange={(e) => setF({ ...f, baseUrl: e.target.value })} />
          </Field>
          <p className="faint small" style={{ margin: 0 }}>Prices in dollars per million tokens, for an account with its own rate. Empty means list price.</p>
          {f.models.map((m) => {
            const p = f.prices[m] ?? { input: '', output: '' };
            const set = (patch: Partial<typeof p>) => setF({ ...f, prices: { ...f.prices, [m]: { ...p, ...patch } } });
            return (
              <div key={m} className="price-row">
                <span className="mono">{m}</span>
                <Field label={<>Input<span className="sr-only"> price for {m}</span></>} error={errors[`price-${m}`]}><input className={fieldClass} type="number" min={0} step="0.01" value={p.input} onChange={(e) => set({ input: e.target.value })} /></Field>
                <Field label={<>Output<span className="sr-only"> price for {m}</span></>}><input className={fieldClass} type="number" min={0} step="0.01" value={p.output} onChange={(e) => set({ output: e.target.value })} /></Field>
              </div>
            );
          })}
        </div>
      </details>
      <div className="row" style={{ gap: 8 }}>
        <Button type="submit" disabled={busy}>{busy ? 'Saving…' : existing ? 'Save changes' : 'Add connection'}</Button>
        <Button variant="ghost" type="button" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}
